import {
  ClientHomework,
  GetStudioClientRes,
  Homework,
  HomeworkStatusUpdateData,
  HomeworkSubmitData,
  IClientHomeworkDetail,
  IHomeworkApiCall,
  IHomeworkWithInfo,
  IRestApiService
} from "../types";
import { HomeworkTracker } from '../services/analytics/apiTrackers';
import { FilePatchOperation } from "types/FilePatch";

const CLASS_HOMEWORK_BASE_URL = 'class-homework';
const STUDIO_CLIENT_HOMEWORK_BASE_URL = 'studio-client-homework';

class HomeworkApiCall implements IHomeworkApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  updateHomeworks(attendanceId: string, homeworks: Partial<Homework>[]): Promise<Homework[]> {
    const startTime = Date.now();

    return this.restApiService.put(`${CLASS_HOMEWORK_BASE_URL}/attendance/${attendanceId}`, {
      homeworks: homeworks,
    })
      .then((result) => {
        HomeworkTracker.trackUpdateHomeworks({
          attendanceId,
          homeworks,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        HomeworkTracker.trackUpdateHomeworks({
          attendanceId,
          homeworks,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getAttendanceHomeworks(attendanceId: string): Promise<Homework[]> {
    return this.restApiService.get(`${CLASS_HOMEWORK_BASE_URL}/attendance/${attendanceId}`);
  }

  getTeacherHomeworks(
    studioId: string,
    ownerId: string,
    teacherId: string,
    from: { format: (format: string) => string },
    to: { format: (format: string) => string }
  ): Promise<IHomeworkWithInfo[]> {
    return this.restApiService.get(
      `${CLASS_HOMEWORK_BASE_URL}/studio/${studioId}/owner/${ownerId}/teacher/${teacherId}?from=${from.format('DD-MM-YYYY')}&to=${to.format('DD-MM-YYYY')}`
    );
  }

  getDetailedHomework(homeworkId: string): Promise<{ homework: Homework, clientHomeworks: IClientHomeworkDetail[] }> {
    return this.restApiService.get(`${CLASS_HOMEWORK_BASE_URL}/homework/${homeworkId}/detailed`);
  }

  getStudioClientHomeworks(studioClientId: string): Promise<GetStudioClientRes> {
    return this.restApiService.get(`${STUDIO_CLIENT_HOMEWORK_BASE_URL}/studio-client/${studioClientId}`);
  }

  upsertStudioClientHomework(studioClientId: string, homeworkId: string, data: FilePatchOperation[]): Promise<GetStudioClientRes[0]> {
    const startTime = Date.now();

    return this.restApiService.put(`${STUDIO_CLIENT_HOMEWORK_BASE_URL}/studio-client/${studioClientId}/homework/${homeworkId}`, { patch: data })
      .then((result) => {
        HomeworkTracker.trackUpsertStudioClientHomework({
          studioClientId,
          homeworkId,
          submitData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        HomeworkTracker.trackUpsertStudioClientHomework({
          studioClientId,
          homeworkId,
          submitData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  submitStudioClientHomework(studioClientId: string, homeworkId: string, data: HomeworkSubmitData): Promise<ClientHomework> {
    const startTime = Date.now();

    return this.restApiService.post(`${STUDIO_CLIENT_HOMEWORK_BASE_URL}/studio-client/${studioClientId}/homework/${homeworkId}/submit`, data)
      .then((result) => {
        HomeworkTracker.trackSubmitStudioClientHomework({
          studioClientId,
          homeworkId,
          submitData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        HomeworkTracker.trackSubmitStudioClientHomework({
          studioClientId,
          homeworkId,
          submitData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  updateStudioClientHomeworkStatus(homeworkId: string, data: HomeworkStatusUpdateData): Promise<ClientHomework> {
    const startTime = Date.now();

    return this.restApiService.patch(`${STUDIO_CLIENT_HOMEWORK_BASE_URL}/${homeworkId}`, data)
      .then((result) => {
        HomeworkTracker.trackUpdateStudioClientHomeworkStatus({
          homeworkId,
          statusData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        HomeworkTracker.trackUpdateStudioClientHomeworkStatus({
          homeworkId,
          statusData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  updateHomework(homeworkId: string, data: Partial<Homework>): Promise<Homework> {
    const startTime = Date.now();

    return this.restApiService.patch(`${CLASS_HOMEWORK_BASE_URL}/${homeworkId}`, data)
      .then((result) => {
        HomeworkTracker.trackUpdateHomework({
          homeworkId,
          homeworkData: data,
          success: true,
          duration: Date.now() - startTime
        });
        return result;
      })
      .catch((error) => {
        HomeworkTracker.trackUpdateHomework({
          homeworkId,
          homeworkData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime
        });
        throw error;
      });
  }

  getUploadUrls(attendanceId: string, files: File[]): Promise<string[]> {
    return this.restApiService.post(`${CLASS_HOMEWORK_BASE_URL}/upload-urls`, {
      attendanceId,
      files: files.map(file => file.name),
    });
  }

  getClientHomeworkUploadUrls(studioClientId: string, homeworkId: string, fileNames: string[]): Promise<string[]> {
    return this.restApiService.post(`${STUDIO_CLIENT_HOMEWORK_BASE_URL}/studio-client/${studioClientId}/homework/${homeworkId}/upload-urls`, {
      files: fileNames,
    });
  }

  deleteHomework(attendanceId: string, homeworkId: string): Promise<void> {
    return this.restApiService.delete(`${CLASS_HOMEWORK_BASE_URL}/attendance/${attendanceId}/homework/${homeworkId}`)
  }
}

export default HomeworkApiCall;
